import {QueryList} from '@angular/core';
import {BehaviorSubject, EMPTY, from, merge, Observable, of} from 'rxjs';
import {debounceTime, map, mapTo, startWith, switchMap, take, tap} from 'rxjs/operators';

export function ObservableFromSource<T>(value: Observable<T> | Promise<T> | T): Observable<T> {
    if (value instanceof Observable) {
        return value;
    }

    if (value instanceof Promise) {
        return from(value);
    }

    // any other value
    return of(value);
}

export function waitFirstEmit<T>(obs: Observable<T>): Promise<Observable<T>> {
    return obs
        .pipe(take(1))
        .toPromise()
        .then(() => obs);
}

export function queryListToObservable<T extends {changed$: Observable<any>}>(queryList: QueryList<T>): Observable<Array<T>> {
    return queryList.changes.pipe(
        startWith(queryList),
        map((list: QueryList<T>) => list.toArray()),
        switchMap(items =>
            merge(...items.map(tab => (tab.changed$ ? tab.changed$ : of(tab)))).pipe(debounceTime(1), mapTo(items), startWith(items))
        )
    );
}

export function pendingObservable<T>(obs: Observable<T>, pending$: BehaviorSubject<boolean>): Observable<T> {
    if (pending$.getValue() === true) {
        return EMPTY;
    }
    pending$.next(true);
    const requestObservable$ = obs.pipe(
        tap(
            () => null,
            () => pending$.next(false),
            () => pending$.next(false)
        )
    );

    // to add teardown logic if unsubscribe while sending request
    return new Observable(subscriber => {
        const sub = requestObservable$.subscribe(subscriber);
        return () => {
            sub.unsubscribe();
            pending$.next(false);
        };
    });
}
